import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, BookOpen } from 'lucide-react';
import { getBundleById } from '../data/bundles';
import { getScenarioById } from '../data/scenarios';
import { getCategoryById } from '../data/categories';
import { useAppStore } from '../store/appStore';
import { useContentAccess } from '../hooks/useContentAccess';
import GuestGate from '../components/GuestGate';
import PremiumGate from '../components/PremiumGate';
import type { AgeGroup } from '../types';

const AGE_COLORS: Record<AgeGroup, string> = {
  '4-6': 'bg-green-100 text-green-700',
  '7-9': 'bg-blue-100 text-blue-700',
  '10-12': 'bg-purple-100 text-purple-700',
  all: 'bg-gray-100 text-gray-600',
};

export default function BundlePage() {
  const { bundleId } = useParams<{ bundleId: string }>();
  const navigate = useNavigate();
  const setPracticeSession = useAppStore((s) => s.setPracticeSession);
  const { isGuest, canPractise } = useContentAccess();
  const [showGuestGate, setShowGuestGate] = useState(false);
  const [showPremiumGate, setShowPremiumGate] = useState(false);

  const bundle = bundleId ? getBundleById(bundleId) : undefined;
  const category = bundle ? getCategoryById(bundle.categoryId) : undefined;

  const scenarios = bundle
    ? bundle.scenarioIds
        .map((id) => getScenarioById(id))
        .filter((s): s is NonNullable<typeof s> => s !== undefined)
    : [];

  if (!bundle) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen px-6 text-center">
        <div className="text-5xl mb-4">😕</div>
        <h2 className="font-display text-xl font-bold text-gray-700 mb-2">
          Bundle not found
        </h2>
        <button
          onClick={() => navigate('/library')}
          className="mt-4 text-primary-600 font-semibold text-sm"
        >
          Back to Library
        </button>
      </div>
    );
  }

  const handleStartPractice = () => {
    if (isGuest) { setShowGuestGate(true); return; }
    if (!canPractise()) { setShowPremiumGate(true); return; }
    setPracticeSession({
      bundleId: bundle.id,
      categoryId: bundle.categoryId,
      scenarioIds: bundle.scenarioIds,
      currentIndex: 0,
      practicedIds: [],
      startedAt: new Date().toISOString(),
    });
    navigate('/practice');
  };

  return (
    <div className="min-h-screen bg-cream pb-40">
      {/* Hero */}
      <div className={`bg-gradient-to-br ${bundle.color} px-4 pt-12 pb-8`}>
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-white/80 hover:text-white mb-4 text-sm font-semibold transition-colors"
        >
          <ChevronLeft size={18} />
          Back
        </button>

        <div className="text-5xl mb-3">{bundle.icon}</div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-white leading-tight mb-2">
          {bundle.name}
        </h1>
        <p className="text-white/80 text-sm leading-relaxed mb-4">
          {bundle.description}
        </p>

        <div className="flex gap-3">
          <span className="bg-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1.5">
            <BookOpen size={12} />
            {scenarios.length} scenario{scenarios.length !== 1 ? 's' : ''}
          </span>
          {category && (
            <span className="bg-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
              {category.icon} {category.name}
            </span>
          )}
        </div>
      </div>

      <div className="px-4 pt-6 max-w-2xl mx-auto">
        {/* Scenario list */}
        <h2 className="font-display font-bold text-gray-800 text-base mb-4">
          Scenarios in this bundle
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
          {scenarios.map((scenario, i) => (
            <motion.button
              key={scenario.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(`/library/scenario/${scenario.id}`)}
              className="w-full text-left bg-white rounded-2xl p-4 shadow-card hover:shadow-card-hover transition-all duration-200 flex items-start gap-4"
            >
              {/* Number badge */}
              <div
                className={`w-8 h-8 rounded-full bg-gradient-to-br ${bundle.color} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}
              >
                {i + 1}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start gap-2 justify-between mb-1">
                  <h3 className="font-display font-bold text-gray-800 text-sm leading-tight">
                    {scenario.title}
                  </h3>
                  <span
                    className={`flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${
                      AGE_COLORS[scenario.ageGroup]
                    }`}
                  >
                    {scenario.ageGroup}
                  </span>
                </div>
                <p className="text-gray-400 text-xs line-clamp-1 italic">
                  "{scenario.memoryPhrase}"
                </p>
              </div>
              <ChevronRight
                size={14}
                className="text-gray-300 flex-shrink-0 mt-1.5"
              />
            </motion.button>
          ))}
        </div>
      </div>

      {/* Start Practice button — sits above the BottomNav (h-16 / z-50) */}
      <div className="fixed bottom-16 left-0 right-0 p-4 bg-white/90 backdrop-blur-md border-t border-gray-100 z-40">
        <div className="max-w-2xl mx-auto">
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={handleStartPractice}
            className={`w-full bg-gradient-to-r ${bundle.color} text-white py-4 rounded-2xl font-bold text-base shadow-lg flex items-center justify-center gap-2 hover:opacity-90 transition-all`}
          >
            <Play size={20} fill="white" />
            Start Practice Session
            {!canPractise() && (
              <span className="ml-1 bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                ✨ Premium
              </span>
            )}
          </motion.button>
        </div>
      </div>

      <GuestGate visible={showGuestGate} onDismiss={() => setShowGuestGate(false)} />
      <PremiumGate visible={showPremiumGate} feature="practice" onDismiss={() => setShowPremiumGate(false)} />
    </div>
  );
}
